/**
 * ReassignCaptainModal Component
 * Allows admin to select a new team member as captain when the original disconnects
 */

import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  Image,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { TeamMemberOption, LiveQuizParticipant } from "../types";

interface ReassignCaptainModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** The participant being reassigned */
  participant: LiveQuizParticipant | null;
  /** Loading state */
  isLoading: boolean;
  /** List of team members to choose from */
  teamMembers: TeamMemberOption[];
  /** Called when admin selects a new captain */
  onSelectMember: (userId: string) => void;
  /** Called when modal is closed */
  onClose: () => void;
}

/**
 * ReassignCaptainModal - For selecting a new team captain
 */
export const ReassignCaptainModal: React.FC<ReassignCaptainModalProps> = ({
  visible,
  participant,
  isLoading,
  teamMembers,
  onSelectMember,
  onClose,
}) => {
  const colors = useThemeColors();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  
  // Filter out the current (disconnected) captain
  const availableMembers = teamMembers.filter(
    m => m.user_id !== participant?.player_user_id
  );
  
  /**
   * Handle confirm selection
   */
  const handleConfirm = () => {
    if (selectedUserId) {
      onSelectMember(selectedUserId);
    }
  };
  
  /**
   * Render a team member option
   */
  const renderMember = ({ item }: { item: TeamMemberOption }) => {
    const isSelected = selectedUserId === item.user_id;
    const displayName = item.nickname || item.full_name;
    
    return (
      <TouchableOpacity
        onPress={() => setSelectedUserId(item.user_id)}
        className="flex-row items-center p-4 rounded-xl mb-2"
        style={{
          backgroundColor: isSelected ? colors.primary + "20" : colors.surfaceSecondary,
          borderWidth: isSelected ? 2 : 0,
          borderColor: colors.primary,
        }}
        accessibilityLabel={`Selecteer ${displayName} als nieuwe teamkapitein`}
        accessibilityRole="radio"
        accessibilityState={{ selected: isSelected }}
      >
        {/* Avatar */}
        <View 
          className="w-12 h-12 rounded-full items-center justify-center mr-3"
          style={{ backgroundColor: colors.primary + "30" }}
        >
          {item.avatar_url ? (
            <Image 
              source={{ uri: item.avatar_url }} 
              className="w-12 h-12 rounded-full"
            />
          ) : (
            <Text className="text-xl font-bold" style={{ color: colors.primary }}>
              {displayName.charAt(0).toUpperCase()}
            </Text>
          )}
        </View>
        
        {/* Name */}
        <View className="flex-1">
          <Text 
            className="font-semibold"
            style={{ color: colors.text }}
          >
            {displayName}
          </Text>
          {item.nickname && item.full_name !== item.nickname && (
            <Text 
              className="text-sm"
              style={{ color: colors.textSecondary }}
            >
              {item.full_name}
            </Text>
          )}
        </View>
        
        {/* Selection indicator */}
        <View 
          className="w-6 h-6 rounded-full items-center justify-center"
          style={{
            backgroundColor: isSelected ? colors.primary : colors.border,
          }}
        >
          {isSelected && (
            <Ionicons name="checkmark" size={16} color="white" />
          )}
        </View>
      </TouchableOpacity>
    );
  };
  
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View className="flex-1 justify-end bg-black/60">
        <View 
          className="rounded-t-3xl p-6 max-h-[80%]"
          style={{ backgroundColor: colors.surface }}
        >
          {/* Header */}
          <View className="flex-row items-center justify-between mb-4">
            <View className="flex-1">
              <Text 
                className="text-xl font-bold"
                style={{ color: colors.text }}
              >
                Nieuw Teamlid Aanwijzen
              </Text>
              <Text 
                className="text-sm mt-1"
                style={{ color: colors.textSecondary }}
              >
                {participant?.team?.name || "Team"}
              </Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              className="p-2"
              accessibilityLabel="Sluiten"
            >
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>
          
          {/* Info */}
          <View 
            className="flex-row items-center p-4 rounded-xl mb-4"
            style={{ backgroundColor: "#F59E0B20" }}
          >
            <Ionicons name="warning" size={20} color="#F59E0B" />
            <Text 
              className="flex-1 ml-2 text-sm"
              style={{ color: colors.text }}
            >
              {participant?.player?.full_name || participant?.player?.nickname || "De teamkapitein"} is niet meer verbonden. 
              Kies een ander teamlid om verder te spelen.
            </Text>
          </View>
          
          {/* Loading state */}
          {isLoading ? (
            <View className="py-12 items-center">
              <ActivityIndicator size="large" color={colors.primary} />
              <Text 
                className="mt-4"
                style={{ color: colors.textSecondary }}
              >
                Teamleden laden...
              </Text>
            </View>
          ) : availableMembers.length === 0 ? (
            <View className="py-12 items-center">
              <Ionicons name="people" size={48} color={colors.textTertiary} />
              <Text 
                className="mt-4 text-center"
                style={{ color: colors.textSecondary }}
              >
                Geen andere teamleden beschikbaar
              </Text>
            </View>
          ) : (
            <>
              {/* Team members list */}
              <FlatList
                data={availableMembers}
                keyExtractor={(item) => item.user_id}
                renderItem={renderMember}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: 16 }}
              />
              
              {/* Confirm button */}
              <TouchableOpacity
                onPress={handleConfirm}
                disabled={!selectedUserId}
                className="py-4 rounded-xl items-center mt-2"
                style={{ 
                  backgroundColor: selectedUserId ? colors.primary : colors.border,
                }}
                accessibilityLabel="Bevestig selectie"
              >
                <Text 
                  className="font-semibold"
                  style={{ color: selectedUserId ? "white" : colors.textTertiary }}
                >
                  Bevestigen
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

export default ReassignCaptainModal;

